import { Link } from "gatsby";
import React, { ReactNode } from "react";
import { AssetDetails, useAssetCache } from "../context/AssetCacheContext";
import useUrl from "../hooks/useUrl";
import { LinkInfo } from "../models/shared/LinkInfo";
import { isNullOrWhiteSpace, replaceWithUnicode } from "../shared/string";

export type SanityLinkProps = LinkInfo & {
  children?: ReactNode;
  className?: string;
  ["aria-label"]?: string | null;
  onClick?: (url: string | null | undefined) => void;
};

export const renderLinkTitle = (linkInfo: LinkInfo) => {
  if (linkInfo.title?.length) {
    return replaceWithUnicode(linkInfo.title);
  } else {
    return linkInfo.isExternalLink === true
      ? linkInfo.url
      : replaceWithUnicode(linkInfo.reference?.title);
  }
};

export const checkLinkExists = (linkInfo: LinkInfo | undefined) =>
  linkInfo &&
  ((linkInfo.isExternalLink && linkInfo.url?.trim()?.length) ||
    (linkInfo.isExternalLink !== true && linkInfo.reference !== undefined));

const SanityLink: React.FC<SanityLinkProps> = ({
  children,
  className,
  "aria-label": ariaLabel,
  onClick: onClickCallback,
  ...linkInfoProps
}) => {
  const { getFileDetails, getImageDetails } = useAssetCache();

  const url = useUrl({
    ...linkInfoProps
  });

  const processedChildren =
    typeof children === "string" ? replaceWithUnicode(children) : children;

  const handleClick = () => {
    // Send the url/phone number/etc. that was clicked back to parent.
    // Used for GTM tracking.
    onClickCallback?.(url);
  };

  if (
    linkInfoProps.isExternalLink ||
    (!isNullOrWhiteSpace(linkInfoProps.fileReference?.url) &&
      linkInfoProps.fileReference!.url.toLowerCase() === url) ||
    (!isNullOrWhiteSpace(linkInfoProps.imageReference?.url) &&
      linkInfoProps.imageReference!.url.toLowerCase() === url)
  ) {
    let assetDetails: AssetDetails | undefined;

    if ("fileReference" in linkInfoProps && linkInfoProps.fileReference) {
      assetDetails = getFileDetails(linkInfoProps.fileReference._ref);
    } else if ("imageReference" in linkInfoProps && linkInfoProps.imageReference) {
      assetDetails = getImageDetails(linkInfoProps.imageReference._ref);
    }

    return (
      <a
        className={className}
        href={url ?? ""}
        {...(linkInfoProps.isTargetBlank && {
          target: "_blank",
          rel: "noreferrer"
        })}
        onClick={handleClick}
        aria-label={
          ariaLabel ||
          linkInfoProps.title ||
          ("reference" in linkInfoProps
            ? replaceWithUnicode(linkInfoProps.reference?.title)
            : undefined) ||
          assetDetails?.altText ||
          assetDetails?.title ||
          assetDetails?.filename ||
          assetDetails?.originalFilename ||
          undefined
        }
      >
        {processedChildren}
      </a>
    );
  } else {
    return (
      <Link
        className={className}
        onClick={handleClick}
        to={url ?? ""}
        aria-label={ariaLabel || linkInfoProps.title || linkInfoProps.reference?.title}
      >
        {processedChildren}
      </Link>
    );
  }
};

export default SanityLink;
